/**
 * "See them on your car".
 *
 * Takes the shopper's photo, sends it to our own endpoint with the URL of this
 * product's wheel image, and shows what comes back.
 *
 * Two things here are deliberate rather than incidental:
 *
 * 1. The photo is downscaled in the browser before it leaves the device. A
 *    phone camera file is several megabytes and none of that resolution
 *    survives the render, so shrinking first makes the upload faster on a
 *    garage signal, cheaper to process, and smaller to hold in memory.
 *
 * 2. There is no API key anywhere in this file, and there never can be. Theme
 *    assets are public. The endpoint is ours, the key lives on it.
 *
 * 3. Wiring happens exactly once, and a render can only be in flight once. Both
 *    guards exist because every render is a paid call. See connectedCallback.
 */
if (!customElements.get('wheel-visualizer')) {
  customElements.define(
    'wheel-visualizer',
    class WheelVisualizer extends HTMLElement {
      /**
       * connectedCallback runs on every insertion, not once per element, and
       * this element gets re-inserted for real: basic-modal moves itself to
       * document.body when it opens and puts itself back when it closes. So
       * opening the modal ran this a second time and stacked a second set of
       * listeners on the same input. One upload then fired two renders, and the
       * count grew with every open and close. Two paid calls, a race between
       * them, and an error painted over a result that had actually succeeded.
       *
       * The subtree moves intact, so the references and listeners from the
       * first run are still good. There is nothing to redo.
       */
      connectedCallback() {
        if (this.wired) return
        this.wired = true

        this.endpoint = this.dataset.endpoint

        /* The heading, the intro and the progress bar are siblings of this
           element, not children of it: they belong to the panel, and the panel
           is the modal. So those two are looked up from the panel root while
           everything else is looked up from here. Querying them off `this` is
           how the progress bar silently never lit up the first time. */
        const panel = this.closest('.wheel-visualizer') || this

        this.els = {
          input: this.querySelector('[data-visualizer-input]'),
          pick: this.querySelector('[data-visualizer-pick]'),
          working: this.querySelector('[data-visualizer-working]'),
          workingPreview: this.querySelector('[data-visualizer-working-preview]'),
          staged: this.querySelector('[data-visualizer-staged]'),
          preview: this.querySelector('[data-visualizer-preview]'),
          result: this.querySelector('[data-visualizer-result]'),
          intro: panel.querySelector('[data-visualizer-intro]'),
          steps: panel.querySelector('[data-visualizer-steps]'),
          ticks: [...this.querySelectorAll('[data-visualizer-tick]')],
          announce: this.querySelector('[data-visualizer-announce]'),
          output: this.querySelector('[data-visualizer-output]'),
          download: this.querySelector('[data-visualizer-download]'),
          render: this.querySelector('[data-visualizer-render]'),
          error: this.querySelector('[data-visualizer-error]'),
        }
        if (!this.els.input || !this.els.render) return

        this.photo = null

        this.els.input.addEventListener('change', this.onPick.bind(this))
        this.els.render.addEventListener('click', this.onRender.bind(this))
        this.querySelectorAll('[data-visualizer-reset]').forEach((button) => {
          button.addEventListener('click', this.reset.bind(this))
        })

        /* The markup already renders the picker, but nothing has marked the
           first segment of the progress bar. Say where we are out loud. */
        this.show('pick')
      }

      /**
       * Picking a photo IS the ask. There is no confirm step.
       *
       * The button that used to sit between these two things said "put these on
       * my car", which is what the shopper had already told us by choosing a
       * photo of their car on a wheel product page. Every extra tap between the
       * intent and the answer costs conversions, and this one bought nothing.
       *
       * It does mean a mis-picked photo spends a render. The per-visitor and
       * per-store caps in the worker are what bound that, not a confirm button.
       */
      async onPick(event) {
        const file = event.target.files && event.target.files[0]
        if (!file) return
        this.hideError()
        try {
          this.photo = await this.downscale(file)
        } catch (e) {
          this.showError(this.text('error_read'))
          return
        }
        this.els.preview.src = this.photo
        this.els.workingPreview.src = this.photo
        this.onRender()
      }

      /**
       * Longest edge to 1280px, JPEG at 0.85. Big enough that the render has
       * detail to work with, small enough to post over a phone connection.
       */
      downscale(file) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onerror = reject
          reader.onload = () => {
            const img = new Image()
            img.onerror = reject
            img.onload = () => {
              const max = 1280
              const scale = Math.min(1, max / Math.max(img.width, img.height))
              const canvas = document.createElement('canvas')
              canvas.width = Math.round(img.width * scale)
              canvas.height = Math.round(img.height * scale)
              canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
              resolve(canvas.toDataURL('image/jpeg', 0.85))
            }
            img.src = reader.result
          }
          reader.readAsDataURL(file)
        })
      }

      async onRender() {
        if (!this.photo) return
        /* Belt as well as braces. The wiring guard above is the actual fix, but
           a render is money leaving the account, so nothing gets to start a
           second one while the first is still out. */
        if (this.rendering) return
        this.rendering = true
        this.hideError()
        this.els.workingPreview.src = this.photo
        this.els.render.classList.add('btn--loading')
        this.els.render.disabled = true
        this.show('working')
        this.startTicker()

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              photo: this.photo,
              wheelImage: this.dataset.wheelImage,
              productTitle: this.dataset.productTitle,
              productId: this.dataset.productId,
            }),
          })

          const data = await response.json().catch(() => ({}))
          if (!response.ok || !data.image) {
            /* The endpoint's own message when it has one: a rate limit or a
               "we could not find a car in that photo" is more use to a shopper
               than a generic failure. */
            throw new Error(data.error || this.text('error_render'))
          }

          this.els.output.src = data.image
          this.els.download.href = data.image
          this.show('result')
        } catch (e) {
          /* Back to the photo they already gave us, not to an empty picker. A
             retry on a garage signal should not cost them a second upload. */
          this.show('staged')
          this.showError(e.message || this.text('error_render'))
        } finally {
          this.stopTicker()
          this.rendering = false
          this.els.render.classList.remove('btn--loading')
          this.els.render.disabled = false
        }
      }

      /**
       * The line under the photo changes while the render runs, so the wait has
       * someone in it rather than a frozen sentence.
       *
       * It advances and stops on the last message. Looping back to the first
       * after fifteen seconds would tell the shopper the thing had started
       * over, which is the feeling this exists to prevent. A render that takes
       * longer than usual just sits on "Nearly there", which is still true.
       */
      startTicker() {
        this.stopTicker()
        if (this.els.announce) this.els.announce.textContent = this.dataset.announce || ''
        if (this.els.ticks.length < 2) return
        this.showTick(0)
        let i = 0
        this.ticker = setInterval(() => {
          i += 1
          this.showTick(i)
          if (i >= this.els.ticks.length - 1) this.stopTicker()
        }, 3000)
      }

      stopTicker() {
        if (this.ticker) clearInterval(this.ticker)
        this.ticker = null
      }

      showTick(index) {
        this.els.ticks.forEach((el, i) => {
          el.hidden = i !== index
        })
      }

      reset() {
        this.photo = null
        this.els.input.value = ''
        this.hideError()
        this.show('pick')
      }

      show(step) {
        if (step !== 'working') this.stopTicker()
        this.els.pick.hidden = step !== 'pick'
        this.els.working.hidden = step !== 'working'
        this.els.staged.hidden = step !== 'staged'
        this.els.result.hidden = step !== 'result'

        /* The intro tells them what to do with an empty panel. Past that it is
           describing a picture they are already looking at, and on a phone it
           costs three lines the picture wants. */
        if (this.els.intro) this.els.intro.hidden = step !== 'pick'

        /* A failed render is still the second step: they gave us a photo, we
           have not given them a car back. Marking it done would be a lie. */
        if (!this.els.steps) return
        const order = ['pick', 'working', 'result']
        const at = order.indexOf(step === 'staged' ? 'working' : step)
        this.els.steps.querySelectorAll('[data-step]').forEach((el, i) => {
          el.classList.toggle('is-done', i < at)
          el.classList.toggle('is-current', i === at)
        })
        /* The current segment pulses to say "this is where you are, and it is
           still going". On the result step nothing is still going, so a pulsing
           bar would be saying something untrue about finished work. */
        this.els.steps.classList.toggle('is-finished', step === 'result')
      }

      showError(message) {
        this.els.error.textContent = message
        this.els.error.hidden = false
      }

      hideError() {
        this.els.error.hidden = true
      }

      /** Copy lives in the locale file and reaches here as a data attribute,
          because a string hardcoded in an asset cannot be translated or edited. */
      text(key) {
        const map = { error_read: 'errorRead', error_render: 'errorRender' }
        return (this.dataset[map[key]] || '').trim() || 'Something went wrong. Try again.'
      }
    }
  )
}
